import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
    Plus, Search, Filter, Eye, Users, Clock, Download, FileText, 
    Building2, MoreHorizontal, ExternalLink, BarChart2, Layers
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatTime(seconds) {
    if (!seconds) return '0s';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function StatCard({ label, value, icon: Icon, color }) {
    return (
        <div className="bg-white rounded-xl border border-stone-200 p-5 flex items-center justify-between">
            <div>
                <p className="text-xs text-stone-400 font-medium uppercase tracking-wider">{label}</p>
                <p className="text-2xl font-bold text-stone-900 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
            </div>
            <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
                <Icon className="w-6 h-6 text-white" />
            </div>
        </div>
    );
}

function RoomCard({ room, onOpen, onAnalytics }) {
    const navigate = useNavigate();
    
    return (
        <div className="bg-white rounded-xl border border-stone-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-stone-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {room.name}
                        </h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            room.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 
                            room.status === 'draft' ? 'bg-stone-100 text-stone-600' : 
                            'bg-red-50 text-red-600'
                        }`}>
                            {room.status.charAt(0).toUpperCase() + room.status.slice(1)}
                        </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-stone-500">
                        {room.account_name && (
                            <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {room.account_name}
                            </span>
                        )}
                        {room.deal_name && (
                            <span className="flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {room.deal_name}
                            </span>
                        )}
                        <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {room.invitee_count || 0} contacts
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/deal-rooms/${room.room_id}`)}
                        data-testid={`open-room-${room.room_id}`}
                    >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Open Room
                    </Button>
                    <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/deal-rooms/${room.room_id}?tab=analytics`)}
                    >
                        <BarChart2 className="w-4 h-4" />
                    </Button>
                </div>
            </div>
            
            {/* Stats Row */}
            <div className="grid grid-cols-5 gap-4 mb-4 py-3 border-t border-b border-stone-100">
                <div>
                    <p className="text-xs text-stone-400">Total Views</p>
                    <p className="text-lg font-semibold text-stone-900 flex items-center gap-1">
                        <Eye className="w-4 h-4 text-stone-400" />
                        {room.total_views || 0}
                    </p>
                </div>
                <div>
                    <p className="text-xs text-stone-400">Visitors</p>
                    <p className="text-lg font-semibold text-stone-900 flex items-center gap-1">
                        <Users className="w-4 h-4 text-stone-600" />
                        {room.unique_visitors || 0}
                    </p>
                </div>
                <div>
                    <p className="text-xs text-stone-400">Downloads</p>
                    <p className="text-lg font-semibold text-stone-900 flex items-center gap-1">
                        <Download className="w-4 h-4 text-emerald-500" />
                        {room.total_downloads || 0}
                    </p>
                </div>
                <div>
                    <p className="text-xs text-stone-400">Avg. Time</p>
                    <p className="text-lg font-semibold text-stone-900 flex items-center gap-1">
                        <Clock className="w-4 h-4 text-amber-500" />
                        {formatTime(room.avg_time_spent)}
                    </p>
                </div>
                <div>
                    <p className="text-xs text-stone-400">Content Items</p>
                    <p className="text-lg font-semibold text-stone-900 flex items-center gap-1">
                        <Layers className="w-4 h-4 text-violet-500" />
                        {room.content_item_count || 0}
                    </p>
                </div>
            </div>
            
            {/* Recent Activity */}
            {room.recent_visitors && room.recent_visitors.length > 0 && (
                <div>
                    <p className="text-xs text-stone-400 mb-2">Recent Activity</p>
                    <div className="space-y-2">
                        {room.recent_visitors.slice(0, 3).map((visitor, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-stone-50 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-xs font-semibold text-stone-900">
                                        {visitor.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-stone-800">{visitor.name}</p>
                                        <p className="text-xs text-stone-400">
                                            {visitor.total_views} views • {visitor.downloads} downloads • {formatTime(visitor.total_time_spent)} spent
                                        </p>
                                    </div>
                                </div>
                                <span className="text-xs text-stone-400">
                                    Last viewed: {visitor.last_viewed_at ? new Date(visitor.last_viewed_at).toLocaleDateString() : 'Never'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            {/* Sections */}
            {room.section_count > 0 && (
                <div className="mt-3 pt-3 border-t border-stone-100">
                    <p className="text-xs text-stone-400">{room.section_count} Sections</p>
                </div>
            )}
        </div>
    );
}

export default function DealRooms() {
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [rooms, setRooms] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [deals, setDeals] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('none');
    const [createForm, setCreateForm] = useState({
        name: '',
        account_id: '',
        deal_id: '',
        expiration_date: '',
    });

    useEffect(() => {
        loadData();
    }, [search, statusFilter]);

    const loadData = async () => {
        try {
            const headers = getAuthHeader();
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
            
            const [roomsRes, statsRes] = await Promise.all([
                axios.get(`${API}/deal-rooms?${params}`, { headers }),
                axios.get(`${API}/deal-rooms/stats`, { headers }),
            ]);
            setRooms(Array.isArray(roomsRes.data) ? roomsRes.data : (roomsRes.data?.data || []));
            setStats(statsRes.data);
        } catch (err) {
            toast.error('Failed to load deal rooms');
        } finally {
            setLoading(false);
        }
    };

    const loadCreateData = async () => {
        try {
            const headers = getAuthHeader();
            const [accountsRes, dealsRes, templatesRes] = await Promise.all([
                axios.get(`${API}/accounts`, { headers }),
                axios.get(`${API}/deals`, { headers }),
                axios.get(`${API}/templates`, { headers }),
            ]);
            setAccounts(Array.isArray(accountsRes.data) ? accountsRes.data : accountsRes.data.data || []);
            setDeals(Array.isArray(dealsRes.data) ? dealsRes.data : (dealsRes.data?.data || []));
            setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : (templatesRes.data?.data || []));
        } catch {}
    };

    const handleCreate = async () => {
        if (!createForm.name.trim()) {
            toast.error('Please enter a room name');
            return;
        }
        
        try {
            let res;
            if (selectedTemplateId && selectedTemplateId !== 'none') {
                res = await axios.post(`${API}/deal-rooms/from-template/${selectedTemplateId}`, createForm, { headers: getAuthHeader() });
            } else {
                res = await axios.post(`${API}/deal-rooms`, createForm, { headers: getAuthHeader() });
            }
            toast.success('Deal room created');
            setShowCreateModal(false);
            setCreateForm({ name: '', account_id: '', deal_id: '', expiration_date: '' });
            setSelectedTemplateId('none');
            navigate(`/deal-rooms/${res.data.room_id}`);
        } catch (err) {
            toast.error('Failed to create deal room');
        }
    };

    const openCreateModal = () => {
        loadCreateData();
        setShowCreateModal(true);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Deal Rooms
                    </h1>
                    <p className="text-stone-500 text-sm mt-1">Share curated content with prospects and track engagement</p>
                </div>
                <Button 
                    onClick={openCreateModal}
                    data-testid="create-deal-room-btn"
                    className="bg-stone-900 hover:bg-stone-800"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Deal Room
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
                <StatCard label="Total Deal Rooms" value={stats.total_rooms || 0} icon={Layers} color="bg-stone-1000" />
                <StatCard label="Total Views" value={stats.total_views || 0} icon={Eye} color="bg-emerald-500" />
                <StatCard label="Unique Visitors" value={stats.unique_visitors || 0} icon={Users} color="bg-violet-500" />
                <StatCard label="Avg. Time Spent" value={formatTime(stats.avg_time_spent)} icon={Clock} color="bg-amber-500" />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                        placeholder="Search deal rooms..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                        data-testid="search-rooms-input"
                    />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40" data-testid="status-filter">
                        <Filter className="w-4 h-4 mr-2 text-stone-400" />
                        <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Room Cards */}
            {rooms.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-stone-200">
                    <Layers className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                    <h3 className="text-lg font-semibold text-stone-700 mb-2">No deal rooms yet</h3>
                    <p className="text-stone-500 mb-4">Create your first deal room to share content with prospects</p>
                    <Button onClick={openCreateModal} className="bg-stone-900 hover:bg-stone-800">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Deal Room
                    </Button>
                </div>
            ) : (
                <div className="space-y-4">
                    {rooms.map(room => (
                        <RoomCard key={room.room_id} room={room} />
                    ))}
                </div>
            )}

            {/* Create Modal */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Create Deal Room</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {/* Template Selection */}
                        {templates.length > 0 && (
                            <div>
                                <Label>Start from Template (optional)</Label>
                                <Select
                                    value={selectedTemplateId}
                                    onValueChange={(v) => {
                                        setSelectedTemplateId(v);
                                        if (v !== 'none') {
                                            const tpl = templates.find(t => t.template_id === v);
                                            if (tpl && !createForm.name) {
                                                setCreateForm(prev => ({ ...prev, name: tpl.name }));
                                            }
                                        }
                                    }}
                                >
                                    <SelectTrigger className="mt-1" data-testid="template-select">
                                        <SelectValue placeholder="Blank room (no template)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Blank room (no template)</SelectItem>
                                        {templates.map(tpl => (
                                            <SelectItem key={tpl.template_id} value={tpl.template_id}>
                                                {tpl.name} ({tpl.sections?.length || 0} sections)
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {selectedTemplateId !== 'none' && (
                                    <p className="text-xs text-stone-700 mt-1">
                                        Sections and content from this template will be pre-populated.
                                    </p>
                                )}
                            </div>
                        )}
                        <div>
                            <Label>Room Name *</Label>
                            <Input
                                value={createForm.name}
                                onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="e.g., TechVenture AI Transformation"
                                className="mt-1"
                                data-testid="room-name-input"
                            />
                        </div>
                        <div>
                            <Label>Account (optional)</Label>
                            <Select
                                value={createForm.account_id || "none"}
                                onValueChange={(v) => setCreateForm(prev => ({ ...prev, account_id: v === "none" ? "" : v }))}
                            >
                                <SelectTrigger className="mt-1" data-testid="account-select">
                                    <SelectValue placeholder="Select an account" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    {accounts.map(acc => (
                                        <SelectItem key={acc.account_id} value={acc.account_id}>
                                            {acc.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Deal (optional)</Label>
                            <Select
                                value={createForm.deal_id || "none"}
                                onValueChange={(v) => setCreateForm(prev => ({ ...prev, deal_id: v === "none" ? "" : v }))}
                            >
                                <SelectTrigger className="mt-1" data-testid="deal-select">
                                    <SelectValue placeholder="Select a deal" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    {deals.map(deal => (
                                        <SelectItem key={deal.deal_id} value={deal.deal_id}>
                                            {deal.title}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Expiration Date (optional)</Label>
                            <Input
                                type="date"
                                value={createForm.expiration_date}
                                onChange={(e) => setCreateForm(prev => ({ ...prev, expiration_date: e.target.value }))}
                                className="mt-1"
                                data-testid="expiration-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                        <Button onClick={handleCreate} className="bg-stone-900 hover:bg-stone-800" data-testid="confirm-create-btn">
                            Create Room
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
